/**
 * Sterile Mate — Janitorial / EVS Catalog
 *
 * Sourced from sterilemate.com/janitorial-environmental-service-supplies/
 * Images & PDFs are self-hosted under /api/static/janitorial/<slug>/
 * (scraper: /tmp/scrape_janitorial.py). Each product retains a `source` link
 * back to the original sterilemate.com page so the latest spec/datasheet
 * always remains available even if we haven't mirrored that PDF yet.
 */

const ASSET_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/static/janitorial`;
const IMG = (cat, file) => `${ASSET_BASE}/${cat}/${file}`;

export const JANITORIAL_CATEGORIES = [
  {
    slug: "atp",
    name: "ATP & Cleanliness Verification",
    short: "Accurate, fast, reliable cleanliness verification — CleanRead, ATP testing, AI inspection",
    hero: IMG("atp", "atp-device.webp"),
    icon: "Microscope",
    accent: "cyan",
    source: "https://sterilemate.com/atp/",
  },
  {
    slug: "commercial-cleaning-chemicals",
    name: "Commercial Cleaning Chemicals",
    short: "Heavy-duty cleaners, peroxides & specialty surface chemistry",
    hero: IMG("commercial-cleaning-chemicals", "Bio-Power.webp"),
    icon: "FlaskConical",
    accent: "amber",
    source: "https://sterilemate.com/product-category/chemical-management-system/commercial-cleaning-chemicals/",
  },
  {
    slug: "dilution-control",
    name: "Dilution Control",
    short: "Concentrated chemicals + DuoFill / Flex 4 / QuadFill dispensers",
    hero: IMG("dilution-control", "128-E-Fecticide-2L.webp"),
    icon: "Droplets",
    accent: "blue",
    source: "https://sterilemate.com/product-category/chemical-management-system/dilution-control/",
  },
  {
    slug: "restroom-care",
    name: "Restroom Care",
    short: "Bowl cleaners, foaming chemistry & disinfectants for restrooms",
    hero: IMG("restroom-care", "950-Bowl-Cleaner.webp"),
    icon: "Sparkles",
    accent: "indigo",
    source: "https://sterilemate.com/product-category/chemical-management-system/restroom-care/",
  },
  {
    slug: "air-vacuums",
    name: "Air Vacuums",
    short: "705 Series air-powered vacuum systems",
    hero: IMG("air-vacuums", "Air-Vacuums.webp"),
    icon: "Wind",
    accent: "slate",
    source: "https://sterilemate.com/product-category/industrial-floor-cleaning-equipment/air-vacuums/",
  },
  {
    slug: "autonomous-floor-care",
    name: "Autonomous Floor Care",
    short: "Robotic floor scrubbers — set-and-forget cleaning",
    hero: IMG("autonomous-floor-care", "Autonomous-Floor-Care.webp"),
    icon: "Bot",
    accent: "purple",
    source: "https://sterilemate.com/product-category/industrial-floor-cleaning-equipment/autonomous-floor-care/",
  },
  {
    slug: "critical-filter-vacuums",
    name: "Critical Filter Vacuums",
    short: "Hospital-grade HEPA, Mercury Recovery & X-839 Series",
    hero: IMG("critical-filter-vacuums", "Hospital-Vacuum.webp"),
    icon: "ShieldCheck",
    accent: "rose",
    source: "https://sterilemate.com/product-category/industrial-floor-cleaning-equipment/critical-filter-vacuums/",
  },
  {
    slug: "restroom-cleaners",
    name: "Restroom Cleaning Equipment",
    short: "Total Restroom System — pressurized restroom cleaning",
    hero: IMG("restroom-cleaners", "Restroom-Cleaners.webp"),
    icon: "Wrench",
    accent: "teal",
    source: "https://sterilemate.com/product-category/industrial-floor-cleaning-equipment/restroom-cleaners/",
  },
  {
    slug: "walk-behind-floor-scrubbers",
    name: "Walk-Behind Floor Scrubbers",
    short: "EZ Mop 360, Port-a-Scrub 12 & Scrubmaster B5",
    hero: IMG("walk-behind-floor-scrubbers", "EZ-Mop-360-Microscrubber.webp"),
    icon: "Truck",
    accent: "emerald",
    source: "https://sterilemate.com/product-category/industrial-floor-cleaning-equipment/walk-behind-floor-scrubbers-industrial-floor-cleaning-equipment/",
  },
  {
    slug: "walk-behind-sweepers",
    name: "Walk-Behind Sweepers",
    short: "Kleen Sweep 25W, 27 & 28 — battery & manual sweepers",
    hero: IMG("walk-behind-sweepers", "Walk-Behind-Sweepers.webp"),
    icon: "Brush",
    accent: "orange",
    source: "https://sterilemate.com/product-category/industrial-floor-cleaning-equipment/walk-behind-sweepers/",
  },
  {
    slug: "cleansite",
    name: "CleanSite — Power 2-Punch System",
    short: "Actizone® F5 (24-hr protection) + CleanSight™ Pathfinder (visual proof of clean)",
    hero: IMG("cleansite", "cleantsite-image.webp"),
    icon: "Zap",
    accent: "fuchsia",
    source: "https://sterilemate.com/cleansite/",
    pdf: { label: "CleanSite Brochure (PDF)", file: IMG("cleansite", "CleanSight-Sell-Sheet.pdf") },
  },
  {
    slug: "microfiber-addons",
    name: "Microfiber Mopping & Wipes",
    short: "Reusable microfiber flat mops, wipes, aluminum mop frames & telescopic poles — compliments the Filmop system",
    hero: IMG("microfiber-addons", "tm_m_01402_bl_face.png"),
    icon: "Layers",
    accent: "sky",
    source: "https://sterilemate.com/marketplace/janitorial/microfiber-addons",
    pdf: {
      label: 'Flat mop spec sheet — TM-M-01402-BL (PDF)',
      file: `${process.env.REACT_APP_BACKEND_URL}/api/spec-sheets/tm-m-01402-bl.pdf`,
    },
  },
];

export const JANITORIAL_PRODUCTS = [
  // ATP
  { sku: "SM-ATP-001", category: "atp", name: "CleanRead™ Handheld ATP Testing Device", image: IMG("atp", "cleanread-handheld-atp-testing-device.webp"), source: "https://sterilemate.com/cleanread-handheld-atp-testing-device/" },
  { sku: "SM-ATP-002", category: "atp", name: "Next Gen AI Visual Inspection Borescope",  image: IMG("atp", "ai-visual-inspection-borescope.webp"),    source: "https://sterilemate.com/next-gen-ai-visual-inspection-borescope/" },
  { sku: "SM-ATP-003", category: "atp", name: "CleanOps App",                              image: IMG("atp", "cleanops-app.webp"),                       source: "https://sterilemate.com/cleanops-app/" },
  { sku: "SM-ATP-004", category: "atp", name: "Sterile Nasal Flocked Swab",                image: IMG("atp", "sterile-nasal-flocked-swab.webp"),         source: "https://sterilemate.com/product/sterile-nasal-flocked-swab/" },
  { sku: "SM-ATP-005", category: "atp", name: "COVID-19 Viral Transport Medium",           image: IMG("atp", "covid-19-viral-transport-medium.webp"),    source: "https://sterilemate.com/product/covid-19-viral-transport-medium/" },
  { sku: "SM-ATP-006", category: "atp", name: "COVID-19 Collection & Transport Kit",       image: IMG("atp", "covid-19-collection-transport-kit.webp"),  source: "https://sterilemate.com/product/covid-19-collection-transport-kit-viral-transport-medium-flocked-swab/" },
  { sku: "SM-ATP-007", category: "atp", name: "CleanRead™ Reporting Software",             image: IMG("atp", "cleanread-reporting-software.webp"),       source: "https://sterilemate.com/product/cleanread-reporting-software/" },
  { sku: "SM-ATP-008", category: "atp", name: "CleanRead™ Surface Protein Detection Swabs",image: IMG("atp", "cleanread-surface-protein-detection-swabs.webp"), source: "https://sterilemate.com/product/cleanread-surface-protein-detection-swabs/" },
  { sku: "SM-ATP-009", category: "atp", name: "CleanRead™ Aqua ATP Detection Swabs",       image: IMG("atp", "cleanread-aqua-atp-detection-swabs.webp"), source: "https://sterilemate.com/product/cleanread-aqua-atp-detection-swabs/" },
  { sku: "SM-ATP-010", category: "atp", name: "CleanRead™ Surface ATP Detection Swabs",    image: IMG("atp", "cleanread-surface-atp-detection-swabs.webp"), source: "https://sterilemate.com/product/cleanread-surface-atp-detection-swabs/" },

  // Commercial Cleaning Chemicals
  { sku: "SM-CCC-001", category: "commercial-cleaning-chemicals", name: "Bio-Power",                          image: IMG("commercial-cleaning-chemicals", "Bio-Power.webp"),                          source: "https://sterilemate.com/product/bio-power/" },
  { sku: "SM-CCC-002", category: "commercial-cleaning-chemicals", name: "Bio-Shine — Neutral Floor Cleaner",  image: IMG("commercial-cleaning-chemicals", "Bio-Shine-Neutral-Floor-Cleaner.webp"),   source: "https://sterilemate.com/product/bio-shine-neutral-floor-cleaner/" },
  { sku: "SM-CCC-003", category: "commercial-cleaning-chemicals", name: "Double O Seven — Peroxide Cleaner", image: IMG("commercial-cleaning-chemicals", "Double-O-Seven-Peroxide-Cleaner.webp"),   source: "https://sterilemate.com/product/double-o-seven-peroxide-cleaner/" },
  { sku: "SM-CCC-004", category: "commercial-cleaning-chemicals", name: "Multi-Shine — Glass & Surface",     image: IMG("commercial-cleaning-chemicals", "Multi-Shine-Glass-and-Surface-Cleaner.webp"), source: "https://sterilemate.com/product/multi-shine-glass-and-surface-cleaner/" },
  { sku: "SM-CCC-005", category: "commercial-cleaning-chemicals", name: "PP-Gone",                            image: IMG("commercial-cleaning-chemicals", "PP-Gone.webp"),                           source: "https://sterilemate.com/product/pp-gone/" },
  { sku: "SM-CCC-006", category: "commercial-cleaning-chemicals", name: "X-Ray — RTU Peroxide Cleaner",      image: IMG("commercial-cleaning-chemicals", "X-Ray-RTU-Peroxide-Cleaner.webp"),         source: "https://sterilemate.com/product/x-ray-rtu-peroxide-cleaner/" },

  // Dilution Control
  { sku: "SM-DIL-001", category: "dilution-control", name: "128 E-Fecticide",     image: IMG("dilution-control", "128-E-Fecticide-2L.webp"), source: "https://sterilemate.com/product/128-e-fecticide/" },
  { sku: "SM-DIL-002", category: "dilution-control", name: "16 Neutra-Zyme",      image: IMG("dilution-control", "16-Neutra-Zyme.webp"),      source: "https://sterilemate.com/product/16-neutra-zyme/" },
  { sku: "SM-DIL-003", category: "dilution-control", name: "19 Enzy-Clean",       image: IMG("dilution-control", "19-Enzy-Clean.webp"),       source: "https://sterilemate.com/product/19-enzy-clean/" },
  { sku: "SM-DIL-004", category: "dilution-control", name: "2 Multi-Shine",       image: IMG("dilution-control", "2-Multi-Shine.webp"),       source: "https://sterilemate.com/product/2-multi-shine/" },
  { sku: "SM-DIL-005", category: "dilution-control", name: "256 Century",         image: IMG("dilution-control", "256-century-q2L.webp"),     source: "https://sterilemate.com/product/256-century/" },
  { sku: "SM-DIL-006", category: "dilution-control", name: "64 Millennium Q",     image: IMG("dilution-control", "64-Millennium-Q-Liter.webp"),source: "https://sterilemate.com/product/64-millennium-q/" },
  { sku: "SM-DIL-007", category: "dilution-control", name: "DuoFill Dispenser",   image: IMG("dilution-control", "DuoFill-Dispenser.webp"),   source: "https://sterilemate.com/product/duofill-dispenser/" },
  { sku: "SM-DIL-008", category: "dilution-control", name: "Flex 4 Dispenser",    image: IMG("dilution-control", "Flex-4-Dispenser.webp"),    source: "https://sterilemate.com/product/flex-4-dispenser/" },
  { sku: "SM-DIL-009", category: "dilution-control", name: "QuadFill Dispenser",  image: IMG("dilution-control", "QuadFill-Dispenser.webp"),  source: "https://sterilemate.com/product/quadfill-dispenser/" },
  { sku: "SM-DIL-010", category: "dilution-control", name: "U-Fill Foamer",       image: IMG("dilution-control", "U-Fill-Foamer.webp"),       source: "https://sterilemate.com/product/u-fill-foamer/" },
  { sku: "SM-DIL-011", category: "dilution-control", name: "U-Fill Sprayer",      image: IMG("dilution-control", "U-Fill-Sprayer.webp"),      source: "https://sterilemate.com/product/u-fill-sprayer/" },

  // Restroom Care
  { sku: "SM-RST-001", category: "restroom-care", name: "2300 Bowl Cleaner",          image: IMG("restroom-care", "2300-Bowl-Cleaner.webp"),       source: "https://sterilemate.com/product/2300-bowl-cleaner/" },
  { sku: "SM-RST-002", category: "restroom-care", name: "950 Bowl Cleaner",           image: IMG("restroom-care", "950-Bowl-Cleaner.webp"),        source: "https://sterilemate.com/product/950-bowl-cleaner/" },
  { sku: "SM-RST-003", category: "restroom-care", name: "Foamy Mac — Restroom Cleaner",image: IMG("restroom-care", "Foamy-Mac-Restroom-Cleaner.webp"), source: "https://sterilemate.com/product/foamy-mac-restroom-cleaner/" },
  { sku: "SM-RST-004", category: "restroom-care", name: "Microcide TB",               image: IMG("restroom-care", "Microcide-TB.webp"),            source: "https://sterilemate.com/product/microcide-tb/" },
  { sku: "SM-RST-005", category: "restroom-care", name: "Multi-Scrub",                image: IMG("restroom-care", "Multi-Scrub.webp"),             source: "https://sterilemate.com/product/multi-scrub/" },
  { sku: "SM-RST-006", category: "restroom-care", name: "PP-Gone",                    image: IMG("restroom-care", "PP-Gone.webp"),                 source: "https://sterilemate.com/product/pp-gone/" },

  // Air Vacuums
  { sku: "SM-AIR-001", category: "air-vacuums", name: "705 Series — Air Vacuums", image: IMG("air-vacuums", "705-Series-Air-Vacuums.webp"), source: "https://sterilemate.com/product/705-series-air-vacuums/" },

  // Autonomous Floor Care
  { sku: "SM-AUT-001", category: "autonomous-floor-care", name: "Autonomous Floor Scrubber", image: IMG("autonomous-floor-care", "Autonomous-Floor-Care-1.webp"), source: "https://sterilemate.com/product/autonomous-floor-scrubber/" },

  // Critical Filter Vacuums
  { sku: "SM-CFV-001", category: "critical-filter-vacuums", name: "Hospital Vacuum",                 image: IMG("critical-filter-vacuums", "Hospital-Vacuum.webp"),         source: "https://sterilemate.com/product/hospital-vacuum/" },
  { sku: "SM-CFV-002", category: "critical-filter-vacuums", name: "Mercury Recovery Vacuum System", image: IMG("critical-filter-vacuums", "Mercury-Recovery-Vacuum-System.webp"), source: "https://sterilemate.com/product/mercury-recovery-vacuum-system/" },
  { sku: "SM-CFV-003", category: "critical-filter-vacuums", name: "X-839 Vacuum Series",            image: IMG("critical-filter-vacuums", "X-839-Vacuum-Series-01.webp"),  source: "https://sterilemate.com/product/x-839-vacuum-series/" },

  // Restroom Cleaners (Equipment)
  { sku: "SM-RCE-001", category: "restroom-cleaners", name: "Total Restroom System", image: IMG("restroom-cleaners", "Total-Restroom-System-01.webp"), source: "https://sterilemate.com/product/total-restroom-system/" },

  // Walk-Behind Floor Scrubbers
  { sku: "SM-WBF-001", category: "walk-behind-floor-scrubbers", name: "EZ Mop 360 Microscrubber", image: IMG("walk-behind-floor-scrubbers", "EZ-Mop-360-Microscrubber.webp"), source: "https://sterilemate.com/product/ez-mop-360-microscrubber/" },
  { sku: "SM-WBF-002", category: "walk-behind-floor-scrubbers", name: "Port a Scrub 12",          image: IMG("walk-behind-floor-scrubbers", "Port-a-Scrub-12.webp"),         source: "https://sterilemate.com/product/port-a-scrub-12/" },
  { sku: "SM-WBF-003", category: "walk-behind-floor-scrubbers", name: "Scrubmaster B5",           image: IMG("walk-behind-floor-scrubbers", "Scrubmaster-B5-1.webp"),       source: "https://sterilemate.com/product/scrubmaster-b5/" },

  // Walk-Behind Sweepers
  { sku: "SM-WBS-001", category: "walk-behind-sweepers", name: "Kleen Sweep 25W", image: IMG("walk-behind-sweepers", "Kleen-Sweep-25W.webp"), source: "https://sterilemate.com/product/kleen-sweep-25w/" },
  { sku: "SM-WBS-002", category: "walk-behind-sweepers", name: "Kleen Sweep 27",  image: IMG("walk-behind-sweepers", "Kleen-Sweep-27.webp"),  source: "https://sterilemate.com/product/kleen-sweep-27/" },
  { sku: "SM-WBS-003", category: "walk-behind-sweepers", name: "Kleen Sweep 28",  image: IMG("walk-behind-sweepers", "Kleen-Sweep-28.webp"),  source: "https://sterilemate.com/product/kleen-sweep-28/" },

  // CleanSite — Power 2-Punch
  { sku: "SM-CST-001", category: "cleansite", name: "Actizone® F5 — 24-Hr Antimicrobial Disinfectant", image: IMG("cleansite", "actizone-f5.webp"),         source: "https://sterilemate.com/cleansite/", description: "Forms an invisible protective film and continues killing bacteria & viruses for up to 24 hours, even after repeated touches." },
  { sku: "SM-CST-002", category: "cleansite", name: "CleanSight™ Pathfinder — Visual Proof of Clean", image: IMG("cleansite", "cleansight-pathfinder.webp"), source: "https://sterilemate.com/cleansite/", description: "Visual contamination imaging — see what ATP swabs miss. No swabs. No consumables." },

  // Microfiber Mopping & Wipes — compliments the Filmop system. Pricing in cents/each + case totals (freight not included).
  {
    sku: "TM-M-01402-BL",
    category: "microfiber-addons",
    featured: true,
    name: '18" Blue Twist Face Microfiber Flat Mop — Velcro Back',
    image: IMG("microfiber-addons", "tm_m_01402_bl_face.png"),
    source: "https://sterilemate.com/marketplace/janitorial/microfiber-addons",
    description: 'Twist face microfiber yarn with foam insert and white velcro back. 80/20 polyester/polyamide. Reusable, launderable 18" flat mop pad for hospital EVS — seats on standard hook-and-loop frames. Sold by the case, 240/CS.',
    specHighlights: [
      { label: "Item / SKU", value: "TM-M-01402-BL" },
      { label: "Size & color", value: '18" · Blue' },
      { label: "Face", value: "Twist face microfiber yarn" },
      { label: "Composition", value: "80/20 polyester / polyamide" },
      { label: "Insert", value: "Foam insert" },
      { label: "Backing", value: "White velcro (hook & loop)" },
      { label: "Qty & UOM", value: "240 / CS (case)" },
      { label: "Availability", value: "In stock · 3-day shipping" },
    ],
    specSheet: {
      label: "Download spec sheet (PDF)",
      file: `${process.env.REACT_APP_BACKEND_URL}/api/spec-sheets/tm-m-01402-bl.pdf`,
    },
    pricing: {
      case: "Quote by case — 240/CS",
      note: "In stock · 3-day shipping included · no additional tariffs or sales tax on this product.",
    },
  },
  {
    sku: "SM-MFA-001",
    category: "microfiber-addons",
    name: 'MWipes™ Reusable Microfiber Wipes — 10"×12", Blue',
    image: IMG("microfiber-addons", "mwipes_blue.jpg"),
    source: "https://www.microfiberwholesale.com/products/mwipes-reusable-microfiber-wipes?variant=32169066856483",
    description: 'Ultra-sonic cut, 200 GSM split microfiber. 80% polyester / 20% polyamide. Holds 300+ wash cycles. Compliments Filmop microfiber mop systems for high-touch surface cleanup.',
    pricing: {
      unit: "$0.29 / wipe",
      box: "$14.50 / 50-ct box",
      case: "$116.00 / case of 8 boxes (400 wipes)",
      note: "Freight not included.",
    },
  },
  {
    sku: "SM-MFA-002",
    category: "microfiber-addons",
    name: '16" Aluminum Microfiber Mop Frame — Swivel w/ Lock',
    image: IMG("microfiber-addons", "mop_frame_16in.jpg"),
    source: "https://worldwidejanitor.com/custom-orders-and-shipping-c-144/wholesale-bulk-microfiber-c-144_441/16-aluminum-microfiber-mop-frame-swivel-with-lock-feature-case-of-24-p-48883",
    description: 'Lightweight aluminum frame for wet & dry microfiber pads. Swivel head with locking feature — pairs with Filmop or standard hook-and-loop pads. Model CY MP-4015R.',
    pricing: {
      unit: "$11.10 / frame",
      case: "$266.40 / case of 24",
      note: "Freight not included.",
    },
  },
  {
    sku: "SM-MFA-003",
    category: "microfiber-addons",
    name: '70" Telescopic Aluminum Pole — for MP-4215R Frame',
    image: IMG("microfiber-addons", "telescopic_pole_70in.jpg"),
    source: "https://worldwidejanitor.com/custom-orders-and-shipping-c-144/wholesale-bulk-microfiber-c-144_441/70-telescopic-aluminum-poles-with-holes-for-mp4215r-case-of-25-p-48897",
    description: 'Sturdy lightweight telescopic pole, extends to 70". Drilled attachment holes for CY MP-4215R / MP-4015R aluminum frame. Sold by the case only. Model CY TAH-70.',
    pricing: {
      unit: "$9.89 / pole",
      case: "$247.28 / case of 25",
      note: "Freight not included.",
    },
  },
];

export function getProductsByCategory(slug) {
  return JANITORIAL_PRODUCTS.filter(p => p.category === slug);
}
export function getCategoryBySlug(slug) {
  return JANITORIAL_CATEGORIES.find(c => c.slug === slug);
}
